import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import CategoryFilterBar from './CategoryFilterBar';
import { useParams } from 'react-router-dom';
import { FiEdit, FiTrash2 } from 'react-icons/fi';
import PriceItem from './PriceItem';
import PosProductCard from './PosProductCard';
import ProductGrid from './ProductGrid';
import NewVarietyModal from '../components/NewVarietyModal';
import ProductCreationModal from "../components/ProductCreationModal";
import VarietyGroupManager from './VarietyGroupManager';
import VarietyGroupTabs from './VarietyGroupTabs';
import { getProducts, deleteProduct, updateProduct, deleteVariety, getLastStockItemForEachVariety, getLastStockItemsAfterPriceUpdate, uploadFile, getCategories, getBranchTaxes } from '../api';
import { normalizePaginatedResponse } from '../utils/pagination';
import InlineLoading from './InlineLoading';
import useConfirmDialog from '../hooks/useConfirmDialog';
import useProductEvents from '../hooks/useProductEvents';
import { applyProductEvent } from '../utils/productEventPatch';
import useCurrentUser from '../hooks/useCurrentUser';
import { hasPermission } from '../utils/permissions';
import { ALL_VARIETY_GROUP_ID, filterVarietiesByGroup } from '../utils/varietyGroups';

const ProductList = ({
  branchId: branchIdProp,
  embedded = false,
  canReadProducts = true,
  canCreateProduct = true,
  canUpdateProduct = true,
  canDeleteProduct = true,
}) => {
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [productNameInput, setProductNameInput] = useState('');
  const [productImageInput, setProductImageInput] = useState('');
  const [productImageUrl, setProductImageUrl] = useState('');
  const [categoryInput, setCategoryInput] = useState('');
  const [barcodeScopeInput, setBarcodeScopeInput] = useState('global');
  const [isLoading, setIsLoading] = useState(false);
	  const [products, setProducts] = useState([]);
	  const [stockItems, setStockItems] = useState([]);
	  const [search, setSearch] = useState('');
	  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState(null);
  const [taxes, setTaxes] = useState([]);
  const [taxesLoading, setTaxesLoading] = useState(false);
  const [taxesError, setTaxesError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
	  const [varietiesMissingUpdate, setPricesMissingUpdate] = useState([]);
	  const [showOnlyMissingUpdate, setShowOnlyMissingUpdate] = useState(false); // ✅ New state
	const [showAddVarietyModal, setShowAddVarietyModal] = useState(false);
  const [activeVarietyGroupId, setActiveVarietyGroupId] = useState(ALL_VARIETY_GROUP_ID);
	const params = useParams();
	const branchId = branchIdProp ?? params.branchId ?? localStorage.getItem('selectedBranchId');
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [searchVarietyResults, setSearchVarietyResults] = useState([]);
  const [useDefaultTaxesInput, setUseDefaultTaxesInput] = useState(true);
  const [taxIdsInput, setTaxIdsInput] = useState([]);
  const [stockMetadataLoading, setStockMetadataLoading] = useState(false);
  const [stockMetadataLoaded, setStockMetadataLoaded] = useState(false);
  const [stockMetadataError, setStockMetadataError] = useState('');
  const stockMetadataRequestRef = useRef(0);
  const { confirm } = useConfirmDialog();
  const { user } = useCurrentUser();
  const canReadTaxes = hasPermission(user, 'tax.read');
  const canReadStock = hasPermission(user, 'stock.read');
  const selectedProduct = useMemo(() => {
    if (selectedProductId === null || selectedProductId === undefined) return null;
    return products.find((product) => String(product.id) === String(selectedProductId)) || null;
  }, [products, selectedProductId]);
  const applyCatalogEvent = useCallback((event) => {
    setProducts((prev) => {
      const nextProducts = applyProductEvent(prev, event);
      return nextProducts || prev;
    });
  }, []);

  useProductEvents(branchId, applyCatalogEvent);

  const closeSelectedProduct = useCallback(() => {
    setSelectedProductId(null);
    setShowEdit(false);
    setShowAddVarietyModal(false);
    setProductImageInput('');
    setProductImageUrl('');
  }, []);

  useEffect(() => {
    if (selectedProductId === null || selectedProductId === undefined || selectedProduct) return;
    setShowEdit(false);
    setShowAddVarietyModal(false);
  }, [selectedProduct, selectedProductId]);

  useEffect(() => {
    setActiveVarietyGroupId(ALL_VARIETY_GROUP_ID);
  }, [selectedProduct?.id]);

  const selectedProductVarieties = useMemo(() => {
    const varieties = Array.isArray(selectedProduct?.Varieties) ? selectedProduct.Varieties : [];
    return filterVarietiesByGroup(
      varieties,
      selectedProduct?.VarietyGroups,
      activeVarietyGroupId
    );
  }, [selectedProduct?.Varieties, selectedProduct?.VarietyGroups, activeVarietyGroupId]);

  const resolveProductCategoryId = useCallback((product) => {
    return product?.categoryId ?? product?.Category?.id ?? null;
  }, []);
  const categoriesForDisplay = useMemo(
    () => (categories || [])
      .slice()
      .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'es', { sensitivity: 'base' }))
      .map((category) => ({
        ...category,
        label: category?.isActive === false ? `${category.name} (inactiva)` : category?.name,
      })),
    [categories]
  );
  const categoriesToRender = useMemo(() => {
    if (selectedCategory === 'all' || search) return categoriesForDisplay;
    return categoriesForDisplay.filter(
      (category) => String(category.id) === String(selectedCategory)
    );
  }, [categoriesForDisplay, search, selectedCategory]);

  useEffect(() => {
    if (!selectedProduct || showEdit) return;
    setProductNameInput(selectedProduct.productName);
    const resolvedCategoryId = resolveProductCategoryId(selectedProduct);
    setCategoryInput(resolvedCategoryId ? String(resolvedCategoryId) : '');
    setProductImageInput('');
    setProductImageUrl(selectedProduct.productImage || '');
    setBarcodeScopeInput(selectedProduct.barcodeScope || 'global');
    setUseDefaultTaxesInput(selectedProduct.useDefaultTaxes !== false);
    setTaxIdsInput(Array.isArray(selectedProduct.taxIds) ? selectedProduct.taxIds : []);
  }, [selectedProduct, resolveProductCategoryId, showEdit]);

  const stockItemByVarietyId = useMemo(() => {
    const map = new Map();
    (stockItems || []).forEach((item) => {
      if (item?.varietyId !== null && item?.varietyId !== undefined) {
        map.set(Number(item.varietyId), item);
      }
    });
    return map;
  }, [stockItems]);

  const missingUpdateVarietyIds = useMemo(
    () => new Set(
      (varietiesMissingUpdate || [])
        .map((item) => Number(item?.varietyId))
        .filter((id) => Number.isInteger(id))
    ),
    [varietiesMissingUpdate]
  );

	  const loadProducts = useCallback(async () => {
	    if (!canReadProducts || !branchId) return;
	    setLoading(true);
	    setError(null);
	    try {
	      const data = await getProducts(branchId, {
          pageSize: 0,
          includeTotal: false,
          sortBy: 'categoryOrder',
          sortOrder: 'asc',
        });
	      const normalizedProducts = normalizePaginatedResponse(data);
	      setProducts(normalizedProducts.data || []);
	    } catch (err) {
	      console.error('Error fetching prices:', err);
	      setError('Failed to load prices.');
	    } finally {
	      setLoading(false);
	    }
	  }, [branchId, canReadProducts]);

  const loadStockMetadata = useCallback(async () => {
    if (!branchId || !canReadStock || stockMetadataLoaded || stockMetadataLoading) return;
    const requestId = stockMetadataRequestRef.current + 1;
    stockMetadataRequestRef.current = requestId;
    setStockMetadataLoading(true);
    setStockMetadataError('');
    try {
      const [stockItemsResponse, missingUpdateResponse] = await Promise.all([
        getLastStockItemForEachVariety(branchId, { pageSize: 0 }),
        getLastStockItemsAfterPriceUpdate(branchId, { pageSize: 0 }),
      ]);
      if (stockMetadataRequestRef.current !== requestId) return;
      const normalizedStockItems = normalizePaginatedResponse(stockItemsResponse);
      const normalizedMissing = normalizePaginatedResponse(missingUpdateResponse);
      setStockItems(normalizedStockItems.data || []);
      setPricesMissingUpdate(normalizedMissing.data || []);
      setStockMetadataLoaded(true);
    } catch (err) {
      if (stockMetadataRequestRef.current !== requestId) return;
      console.error('Error fetching stock metadata:', err);
      setStockItems([]);
      setPricesMissingUpdate([]);
      setStockMetadataLoaded(true);
      setStockMetadataError('No se pudieron cargar los datos de stock para esta vista.');
    } finally {
      if (stockMetadataRequestRef.current === requestId) {
        setStockMetadataLoading(false);
      }
    }
  }, [branchId, canReadStock, stockMetadataLoaded, stockMetadataLoading]);

	  useEffect(() => {
	    if (!canReadProducts) {
	      setLoading(false);
	      setError('No tienes permisos para ver productos.');
	      return;
	    }
      if (!branchId) {
        setLoading(false);
        setError('Selecciona una sucursal para ver productos.');
        return;
      }
      setProducts([]);
      setStockItems([]);
      setPricesMissingUpdate([]);
      setStockMetadataLoading(false);
      setStockMetadataLoaded(false);
      setStockMetadataError('');
      stockMetadataRequestRef.current += 1;
      closeSelectedProduct();
	    loadProducts();
	  }, [branchId, canReadProducts, closeSelectedProduct, loadProducts]);

  useEffect(() => {
    if (!selectedProduct && !showOnlyMissingUpdate) return;
    loadStockMetadata();
  }, [selectedProduct, showOnlyMissingUpdate, loadStockMetadata]);

  useEffect(() => {
    if (!branchId) return;
    setCategoriesLoading(true);
    setCategoriesError(null);
    getCategories({ includeAvailability: true, branchId })
      .then((data) => {
        setCategories(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error('Error fetching categories:', err);
        setCategories([]);
        setCategoriesError('No se pudieron cargar las categorías.');
      })
      .finally(() => setCategoriesLoading(false));
  }, [branchId]);

  useEffect(() => {
    if (!branchId) return;
    if (!canReadTaxes) {
      setTaxes([]);
      setTaxesError('No tienes permisos para ver impuestos.');
      return;
    }
    setTaxesLoading(true);
    setTaxesError(null);
    getBranchTaxes(branchId)
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        const active = list.filter((tax) => tax.branchActive && tax.isActive !== false);
        setTaxes(active);
      })
      .catch((err) => {
        console.error('Error fetching taxes:', err);
        setTaxes([]);
        setTaxesError('No se pudieron cargar los impuestos.');
      })
      .finally(() => setTaxesLoading(false));
  }, [branchId, canReadTaxes]);

  useEffect(() => {
    if (selectedCategory === 'all') return;
    const isStillValid = categoriesForDisplay.some(
      (category) => String(category.id) === String(selectedCategory)
    );
    if (!isStillValid) {
      setSelectedCategory('all');
    }
  }, [categoriesForDisplay, selectedCategory]);

  const handleProductCreated = useCallback((product) => {
    if (!product?.id) return;
    applyCatalogEvent({
      action: 'create-product',
      productId: product.id,
      product,
    });
  }, [applyCatalogEvent]);

  const handleUpdateProduct = async (id) => {
    if (!canUpdateProduct) return;
    setIsLoading(true);
    try {
      const selectedCategoryData = categories.find(
        (category) => String(category.id) === String(categoryInput)
      );
      const data = {
        productName: productNameInput,
        categoryId: selectedCategoryData?.id,
        barcodeScope: barcodeScopeInput,
        useDefaultTaxes: useDefaultTaxesInput,
        taxIds: useDefaultTaxesInput ? [] : taxIdsInput,
      }
      if (productImageInput) {
        const imageUrl = await uploadFile(productImageInput);
        data.productImage = imageUrl.url;
      }
      const updated = await updateProduct(branchId, id, data);
      const nextProduct = updated?.id ? updated : { id, ...data };
      applyCatalogEvent({
        action: 'update-product',
        productId: id,
        product: nextProduct,
      });
      setShowEdit(false);
    } catch (error) {
      console.error('Error updating product:', error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!canDeleteProduct) return;
    try {
      const approved = await confirm({
        title: 'Eliminar producto',
        message: '¿Estás seguro de que deseas eliminar este producto?',
        confirmLabel: 'Eliminar',
        cancelLabel: 'Cancelar',
        tone: 'danger',
      });
      if (!approved) return;
      setIsLoading(true);
      await deleteProduct(branchId, id);
      applyCatalogEvent({
        action: 'delete-product',
        productId: id,
      });
      if (String(selectedProductId) === String(id)) {
        closeSelectedProduct();
      }
    } catch (error) {
      console.error('Error deleting product:', error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddVariety = async (variety) => {
    try {
      if (!canUpdateProduct || !selectedProductId || !variety) return;
      applyCatalogEvent({
        action: 'add-variety',
        productId: selectedProductId,
        varietyId: variety.id,
        variety,
      });
    } catch (error) {
      console.error('Error adding variety:', error.message);
    }
  };

  const handleUpdateVariety = async (idOrVariety, data) => {
    try {
      if (!canUpdateProduct) return;
      const varietyId = typeof idOrVariety === 'object' ? idOrVariety.id : idOrVariety;
      const updateData = typeof idOrVariety === 'object' ? idOrVariety : data;
      if (!varietyId) return;
      applyCatalogEvent({
        action: 'update-variety',
        varietyId,
        variety: updateData,
      });
    } catch (error) {
      console.error('Error updating variety:', error.message);
    }
  };

	  // ✅ Updated filter function
	  const filterProducts = (searchValue, category) => {
	    let filtered = products;
	    let varietyResults = [];
	    const normalizedSearch = (searchValue || '').trim().toLowerCase();
	
	    if (normalizedSearch) {
	      // Search ignoring category, match only words starting with the search
	      filtered = products.filter((product) =>
	        product.productName
	          .toLowerCase()
	          .split(' ')
	          .some((word) => word.startsWith(normalizedSearch))
	      );

	      // Also search in variety names
	      varietyResults = products.flatMap((product) =>
	        (product.Varieties || [])
	          .filter(
	            (variety) =>
	              variety.varietyName &&
	              variety.varietyName.toLowerCase().includes(normalizedSearch)
	          )
	          .map((variety) => ({ product, variety }))
	      );
	    } else if (category !== 'all') {
	      // Only filter by category if search is empty
	      filtered = products.filter(
          (product) => String(resolveProductCategoryId(product)) === String(category)
        );
	    }
	
	    if (showOnlyMissingUpdate) {
	      filtered = filtered.filter((product) =>
	        product.Varieties?.some((variety) => missingUpdateVarietyIds.has(Number(variety.id)))
	      );

	      if (normalizedSearch) {
	        varietyResults = varietyResults.filter(({ variety }) =>
	          missingUpdateVarietyIds.has(Number(variety.id))
	        );
	      }
	    }
	
	    setFilteredProducts(filtered);
	    setSearchVarietyResults(varietyResults);
	  };

	  useEffect(() => {
	    filterProducts(search, selectedCategory);
	  }, [search, selectedCategory, products, showOnlyMissingUpdate, missingUpdateVarietyIds]);

  const handleDeleteVariety = async (id) => {
    try {
      if (!canUpdateProduct) return;
      const approved = await confirm({
        title: 'Eliminar variedad',
        message: '¿Estás seguro de que deseas eliminar este producto?',
        confirmLabel: 'Eliminar',
        cancelLabel: 'Cancelar',
        tone: 'danger',
      });
      if (!approved) return;
      await deleteVariety(branchId, id);
      applyCatalogEvent({
        action: 'delete-variety',
        varietyId: id,
      });
    } catch (error) {
      console.error('Error deleting product:', error.message);
    }
  };

  if (loading) {
    return (
      <p>
        <InlineLoading />
      </p>
    );
  }
  if (error) return <p>{error}</p>;

  return (
    <>
      {isModalOpen && (
        <ProductCreationModal
          onClose={setIsModalOpen}
          onProductCreated={handleProductCreated}
          canCreateProduct={canCreateProduct}
          categories={categoriesForDisplay}
          taxes={taxes}
        />
      )}
      <NewVarietyModal
        isOpen={showAddVarietyModal}
        product={selectedProduct}
        existingVarieties={selectedProduct?.Varieties || []}
        barcodeScope={selectedProduct?.barcodeScope}
        onClose={() => { setShowAddVarietyModal(false); }}
        onVarietyCreated={handleAddVariety}
        canUpdateProduct={canUpdateProduct}
      />

      <div className={embedded ? "" : "p-4"}>
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-xl font-semibold">Productos</h1>
          {canCreateProduct && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-blue-500 text-white px-4 py-2 rounded shadow hover:bg-blue-600 text-sm"
            >
              + Producto Categoría
            </button>
          )}
        </div>
        {/* ✅ Search and Missing Update Filter */}
        <div className="sticky top-0 pt-2 bg-white z-10 border-b mt-4 pb-2">
          <input
            type="text"
            placeholder="Buscar producto..."
            className="border border-gray-300 rounded p-2 mb-2 w-full"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
          />
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="missingUpdate"
              checked={showOnlyMissingUpdate}
              onChange={(e) => setShowOnlyMissingUpdate(e.target.checked)}
              disabled={!canReadStock || stockMetadataLoading}
              className="w-4 h-4"
            />
            <label htmlFor="missingUpdate" className="text-sm">Mostrar solo productos por actualizar</label>
          </div>
          {showOnlyMissingUpdate && stockMetadataLoading && (
            <div className="mt-2">
              <InlineLoading label="Buscando productos por actualizar" />
            </div>
          )}
          {!canReadStock && (
            <p className="mt-1 text-xs text-gray-500">
              Necesitas permiso de stock para filtrar productos por actualizar.
            </p>
          )}
          {stockMetadataError && (
            <p className="mt-1 text-xs text-red-600">{stockMetadataError}</p>
          )}
        </div>

        <CategoryFilterBar
          categories={categoriesForDisplay}
          selectedCategory={selectedCategory}
          onSelect={setSelectedCategory}
          includeAll
          allItem={{ id: 'all', label: 'Todos', initial: 'T' }}
          loading={categoriesLoading}
          loadingMessage={<InlineLoading label="Cargando categorías" />}
          error={categoriesError}
          emptyMessage="No hay categorías disponibles."
          isEmpty={categoriesForDisplay.length === 0}
          itemsWrapperClassName="mb-6"
        />

	        {/* Search Results Category */}
	        {search && searchVarietyResults.length > 0 && (
	          <div className="space-y-4 mb-6">
	            <div className="flex items-center gap-4 my-6">
	              <div className="flex-grow border-t border-gray-300"></div>
	              <h2 className="text-lg font-bold text-gray-700 whitespace-nowrap">
	                Resultados de Búsqueda
	              </h2>
	              <div className="flex-grow border-t border-gray-300"></div>
	            </div>

	            <ProductGrid>
	              {searchVarietyResults.map(({ product, variety }) => (
	                <PosProductCard
	                  key={`${product.id}-${variety.id}`}
                    productName={product.productName}
                    subtitle={variety.varietyName}
                    imageSrc={variety.varietyImage || product.productImage || 'https://placehold.co/200'}
	                  onClick={() => setSelectedProductId(product.id)}
	                />
	              ))}
	            </ProductGrid>
	          </div>
	        )}

        {/* Filtered Prices */}
        <div className="space-y-8">
          {categoriesToRender.map((category) => {
            const productsInCategory = filteredProducts
              .filter((p) => resolveProductCategoryId(p) === category.id)
              .sort((a, b) => a.productName.localeCompare(b.productName)); // Alphabetical

            if (productsInCategory.length === 0) return null;

            return (
              <div key={category.id}>
                {/* Category Title */}
                <div className="flex items-center gap-4 my-6">
                  <div className="flex-grow border-t border-gray-300"></div>
                  <h2 className="text-lg font-bold text-gray-700 whitespace-nowrap">
                    {category.name}
                  </h2>
                  <div className="flex-grow border-t border-gray-300"></div>
                </div>

                {/* Products Grid */}
                <ProductGrid>
                  {productsInCategory.map((product) => (
                    <PosProductCard
                      key={product.id}
                      productName={product.productName}
                      imageSrc={product.productImage || 'https://placehold.co/200'}
                      onClick={() => setSelectedProductId(product.id)}
                    />
                  ))}
                </ProductGrid>
              </div>
            );
          })}
        </div>

        {!loading && products.length > 0 && (
          <div className="text-sm text-gray-400 text-center mt-4">
            Ya se cargaron todos los productos.
          </div>
        )}
        {/* Modal for Varieties */}
        {selectedProduct && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40"
            onClick={(event) => event.stopPropagation()}
          >
            <div
              className="bg-white p-4 sm:p-6 rounded-lg shadow-lg max-w-3xl w-full mx-4 relative flex flex-col"
              style={{ maxHeight: "80vh", display: "flex" }} // Ensures the modal does not exceed 80% of the screen height
              onClick={(e) => e.stopPropagation()}
            >
              {/* Scrollable Content */}
              <div className="overflow-y-auto px-2" style={{ maxHeight: "70vh" }}>
                {/* Close Button */}
                <button
                  onClick={closeSelectedProduct}
                  className="absolute top-2 right-2 text-gray-600 hover:text-black bg-white p-1 rounded-full"
                >
                  ✕
                </button>

                {/* Centered Image with Increased Size */}
                <div className="flex justify-center items-center mb-4">
                  <img
                    src={productImageUrl || selectedProduct.productImage || "https://placehold.co/300"}
                    alt={selectedProduct.productName}
                    className="w-20 h-20 sm:w-28 sm:h-28 object-cover rounded-lg"
                  />
                </div>

                {/* Image Upload */}
                {showEdit && (
                  <div className="w-80 flex flex-col mb-3">
                    <label className="block text-left font-medium">Imagen del Producto:</label>
                    <input
                      type="file"
                      id="productImage"
                      name="productImage"
                      onChange={(e) => {
                        setProductImageInput(e.target.files[0]);
                        setProductImageUrl(URL.createObjectURL(e.target.files[0]));
                      }}
                      accept="image/*"
                      className="border border-gray-300 rounded px-2 py-1 w-full"
                    />
                  </div>
                )}

                {/* Product Name */}
                {showEdit ? (
                  <div className="w-80 flex flex-col mb-3">
                    <label className="block text-left font-medium">Nombre del Producto:</label>
                    <input
                      type="text"
                      id="productName"
                      name="productName"
                      value={productNameInput}
                      onChange={(e) => setProductNameInput(e.target.value)}
                      required
                      autoComplete="off"
                      className="border border-gray-300 rounded px-2 py-1 w-full"
                    />
                  </div>
                ) : (
                  <h2 className="text-xl font-bold mb-2 text-center">{selectedProduct.productName}</h2>
                )}

                {/* Category Selection */}
                {showEdit ? (
                  <div className="w-80 flex flex-col mb-3">
                    <label className="block text-left font-medium">Categoría:</label>
                    <select
                      id="categoryId"
                      name="categoryId"
                      required
                      value={categoryInput}
                      onChange={(e) => setCategoryInput(e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1 w-full"
                    >
                      <option value="">Selecciona una categoría</option>
                      {categoriesForDisplay.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <p className="text-md mb-2 text-center">
                    <strong>Categoría:</strong>{" "}
                    {(() => {
                      const resolvedId = resolveProductCategoryId(selectedProduct);
                      const resolvedCategory = categoriesForDisplay.find(
                        (category) => category.id === resolvedId
                      );
                      return resolvedCategory?.name || selectedProduct.Category?.name || 'Sin categoría';
                    })()}
                  </p>
                )}

                {/* Barcode Scope */}
                {showEdit ? (
                  <div className="w-80 flex flex-col mb-3">
                    <label className="block text-left font-medium">Alcance del código de barras:</label>
                    <select
                      id="barcodeScope"
                      name="barcodeScope"
                      value={barcodeScopeInput}
                      onChange={(e) => setBarcodeScopeInput(e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1 w-full"
                    >
                      <option value="global">Global</option>
                      <option value="per_variety">Por variedad</option>
                    </select>
                  </div>
                ) : (
                  <p className="text-md mb-2 text-center">
                    <strong>Barcode:</strong>{" "}
                    {selectedProduct.barcodeScope === "per_variety" ? "Por variedad" : "Global"}
                  </p>
                )}

                {showEdit && (
                  <div className="w-80 flex flex-col mb-3">
                    <label className="block text-left font-medium">Impuestos:</label>
                    {!canReadTaxes && (
                      <span className="text-xs text-gray-500">No tienes permisos para ver impuestos.</span>
                    )}
                    {canReadTaxes && taxesLoading && (
                      <InlineLoading label="Cargando impuestos" />
                    )}
                    {canReadTaxes && !taxesLoading && taxesError && (
                      <span className="text-xs text-red-600">{taxesError}</span>
                    )}
                    {canReadTaxes && !taxesLoading && (
                      <>
                        <label className="flex items-center gap-2 text-sm text-gray-700 mt-2">
                          <input
                            type="checkbox"
                            checked={useDefaultTaxesInput}
                            onChange={(e) => setUseDefaultTaxesInput(e.target.checked)}
                          />
                          Usar impuestos por defecto (IVA)
                        </label>
                        {!useDefaultTaxesInput && (
                          <div className="flex flex-wrap gap-3 mt-2">
                            {taxes.length === 0 && (
                              <span className="text-xs text-gray-500">No hay impuestos activos.</span>
                            )}
                            {taxes.map((tax) => (
                              <label key={tax.id} className="flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  checked={taxIdsInput.includes(tax.id)}
                                  onChange={(e) => {
                                    const next = new Set(taxIdsInput);
                                    if (e.target.checked) {
                                      next.add(tax.id);
                                    } else {
                                      next.delete(tax.id);
                                    }
                                    setTaxIdsInput(Array.from(next));
                                  }}
                                />
                                {tax.name} ({Number(tax.rate || 0)}%)
                              </label>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-between items-center w-full pt-4 mb-2">
                  {!showEdit && canUpdateProduct && (
                    <button
                      className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
                      onClick={() => setShowAddVarietyModal(true)}
                      disabled={showEdit}
                    >
                      + Agregar Tipo
                    </button>
                  )}

                  <div className="flex gap-2 ml-auto">
                    {showEdit ? (
                      <div className="flex gap-2">
                        <button
                          className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
                          onClick={() => handleUpdateProduct(selectedProduct.id)}
                          disabled={isLoading}
                        >
                          Guardar
                        </button>
                        <button
                          className="bg-gray-500 text-white px-3 py-1 rounded hover:bg-gray-600"
                          onClick={() => setShowEdit(false)}
                          disabled={isLoading}
                        >
                          Cancelar
                        </button>
                      </div>
                    ) : (
                      <>
                        {canUpdateProduct && (
                          <button
                            className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
                            onClick={() => setShowEdit(!showEdit)}
                            disabled={showEdit || isLoading}
                          >
                            <FiEdit size={20} />
                          </button>
                        )}
                        {canDeleteProduct && (
                          <button
                            className="bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600 flex justify-center"
                            onClick={() => handleDeleteProduct(selectedProduct.id)}
                            disabled={showEdit || isLoading}
                          >
                            <FiTrash2 size={20} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {!showEdit && (
                  <VarietyGroupManager
                    branchId={branchId}
                    product={selectedProduct}
                    canUpdateProduct={canUpdateProduct}
                  />
                )}

                {/* Varieties Grid */}
                {!showEdit && (
                  <VarietyGroupTabs
                    groups={selectedProduct?.VarietyGroups}
                    activeGroupId={activeVarietyGroupId}
                    onSelect={setActiveVarietyGroupId}
                    className="mb-4"
                  />
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {!showEdit &&
                    selectedProductVarieties.map((variety) => (
                      <PriceItem
                        key={variety.id}
                        id={variety.id}
                        name={variety.varietyName}
                        productImage={variety.varietyImage}
                        prices={variety.VarietyPrices || []}
                        description={variety.description}
                        priceAutoRuleOverride={variety.priceAutoRuleOverride || null}
                        barcodeScope={selectedProduct?.barcodeScope}
                        allVarieties={selectedProduct?.Varieties || []}
                        lastStockItem={stockItemByVarietyId.get(Number(variety.id))}
                        missingUpdate={missingUpdateVarietyIds.has(Number(variety.id))}
                        onDeleteVariety={handleDeleteVariety}
                        onVarietyUpdate={handleUpdateVariety}
                        canUpdateProduct={canUpdateProduct}
                      />
                    ))}
                  {!showEdit && selectedProductVarieties.length === 0 && (
                    <p className="text-sm text-gray-500">No hay variedades para este grupo.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ProductList;
